import { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";

import { UseMutateFunction } from "@tanstack/react-query";

import {
  useAddImportDatasetMutation,
  useImportDatasetMutation,
  useReplaceImportDatasetMutation
} from "@/api/dataset/useDatasetAPI";
import { FilePlusIcon, InformationIcon, SpinnerIcon } from "@/assets/icons";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { MESSAGE } from "@/constants/message";
import { useDatadogAnalytics } from "@/hooks/tracker/useDatadogAnalytics";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { ExportDatasetFileType } from "@/types/dataset";

import NotificationContent from "../notification/notificationContent";
import { Alert, AlertDescription } from "../ui/alert";
import { getUploadErrorDisplay } from "./utils";

export enum EUploadDatasetType {
  NEW = "new",
  ADD_MORE_WITH_DUPLICATE = "addmorewithduplicate",
  ADD_MORE = "addmore",
  REPLACE_ALL = "replace",
  DELETE = "delete"
}

export type UploadDatasetDialogType =
  | EUploadDatasetType.NEW
  | EUploadDatasetType.ADD_MORE
  | EUploadDatasetType.REPLACE_ALL
  | EUploadDatasetType.DELETE
  | EUploadDatasetType.ADD_MORE_WITH_DUPLICATE;

const UPLOAD_ERROR_MESSAGE = {
  [EUploadDatasetType.NEW]:
    "Sorry, we’re experiencing an issue while processing your request. Please try again.",
  [EUploadDatasetType.ADD_MORE]:
    "Sorry, we’re experiencing an issue adding data to your dataset. Please try again.",
  [EUploadDatasetType.REPLACE_ALL]:
    "Sorry, we’re experiencing an issue replacing data in your dataset. Please try again.",
  [EUploadDatasetType.DELETE]:
    "Sorry, we’re experiencing an issue deleting your dataset. Please try again.",
  [EUploadDatasetType.ADD_MORE_WITH_DUPLICATE]:
    "Sorry, we’re experiencing an issue adding data with duplicates to your dataset. Please try again."
};

const title = {
  [EUploadDatasetType.ADD_MORE]: "Add More Data",
  [EUploadDatasetType.REPLACE_ALL]: "Replace Data",
  [EUploadDatasetType.NEW]: "Upload Dataset",
  [EUploadDatasetType.DELETE]: "Delete Dataset",
  [EUploadDatasetType.ADD_MORE_WITH_DUPLICATE]:
    "Add More Data (Allow Duplicates)"
};

const allowedFileTypes: ExportDatasetFileType[] = [
  ExportDatasetFileType.CSV,
  ExportDatasetFileType.XLSX,
  ExportDatasetFileType.KML,
  ExportDatasetFileType.GEOJSON,
  ExportDatasetFileType.SHAPEFILE
];

const fileTypeLabels: Record<ExportDatasetFileType, string> = {
  [ExportDatasetFileType.CSV]: "CSV",
  [ExportDatasetFileType.XLSX]: "XLSX",
  [ExportDatasetFileType.GEOJSON]: "GeoJSON",
  [ExportDatasetFileType.SHAPEFILE]: "Shapefile",
  [ExportDatasetFileType.KML]: "KML"
};

export interface UploadDialogProps {
  uploadType?: UploadDatasetDialogType;
  setIsLoading?: (loading: boolean) => void;
  setOperationId: (id: string) => void;
  setIsParsingActive: (active: boolean) => void;
  setIsKmlUpload?: (isKml: boolean) => void;
  onUploadSuccess?: (uploadType: UploadDatasetDialogType) => void;
  datasetId?: string;
  /**
   * If set to true, the upload dialog will not dismiss automatically on
   * Escape, outside interaction, or focus leaving its content. Used by the
   * onboarding tour, which owns the dialog lifecycle. Defaults to false.
   */
  preventAutoDismiss?: boolean;
  disableAnimation?: boolean;
}

export default function UploadDialog({
  uploadType = EUploadDatasetType.NEW,
  setIsLoading,
  setOperationId,
  setIsParsingActive,
  setIsKmlUpload,
  onUploadSuccess,
  datasetId,
  preventAutoDismiss = false,
  disableAnimation = false
}: Readonly<UploadDialogProps>) {
  const { trackAction } = useDatadogAnalytics();
  const { showToast } = useToast();
  const {
    isGeoJsonUploadEnabled,
    isShapefileUploadEnabled,
    isKMLUploadEnabled
  } = useAppFeatures();

  const [abortController, setAbortController] =
    useState<AbortController | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  let useImportDataset;

  switch (uploadType) {
    case EUploadDatasetType.NEW:
      useImportDataset = useImportDatasetMutation;
      break;
    case EUploadDatasetType.ADD_MORE:
      useImportDataset = useAddImportDatasetMutation;
      break;
    case EUploadDatasetType.REPLACE_ALL:
      useImportDataset = useReplaceImportDatasetMutation;
      break;
    default:
      useImportDataset = useImportDatasetMutation;
  }

  const uploadMutation = useImportDataset({
    onMutate: () => {
      setIsUploading(true);
    },
    onSuccess: (data, payload) => {
      const formData = payload.file;
      const files = isShapefileUploadEnabled
        ? (formData.getAll("files") as File[])
        : [formData.get("file") as File];
      const fileSize = files.reduce((acc, f) => acc + (f?.size ?? 0), 0);

      const isKmlUpload = files.some((f) =>
        f?.name?.toLowerCase().endsWith(".kml")
      );
      setIsKmlUpload?.(isKmlUpload);

      trackAction("dataset_upload", {
        upload_type: uploadType,
        file_size: fileSize
      });
      setAbortController(null);
      setOperationId(data.operation_id);
      setIsParsingActive(true);
      setIsUploading(false);
      onUploadSuccess?.(uploadType);
    },
    onError: (error) => {
      const { title, description } = getUploadErrorDisplay(
        error,
        uploadType,
        UPLOAD_ERROR_MESSAGE
      );

      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent title={title} description={description} />
        ),
        autoClose: 2000
      });

      setIsParsingActive(false);
      setIsUploading(false);
      setAbortController(null);
    }
  });

  const acceptedFileTypes: Record<string, string[]> = {
    "text/csv": [".csv"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx"
    ],
    ...(isGeoJsonUploadEnabled && {
      "application/geo+json": [".geojson"],
      "application/json": [".geojson"]
    }),
    ...(isShapefileUploadEnabled && {
      "application/octet-stream": [".shp", ".shx", ".dbf", ".prj", ".cpg"],
      "application/zip": [".zip"]
    }),
    ...(isKMLUploadEnabled && {
      "application/vnd.google-earth.kml+xml": [".kml"]
    })
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: acceptedFileTypes,
    maxSize: 100 * 1024 * 1024, // 100MB
    onDropRejected: (fileRejections) => {
      fileRejections.forEach((rejection) => {
        const { errors } = rejection;
        errors.forEach((error) => {
          if (error.code === "file-too-large") {
            showToast({
              type: EToastType.ERROR,
              message: (
                <NotificationContent
                  title={MESSAGE.DATASET_UPLOAD_ERROR_FILE_TOO_LARGE_TITLE}
                  description={
                    MESSAGE.DATASET_UPLOAD_ERROR_FILE_TOO_LARGE_DESCRIPTION
                  }
                />
              ),
              autoClose: 2000
            });
          }
          if (error.code === "file-invalid-type") {
            showToast({
              type: EToastType.ERROR,
              message: (
                <NotificationContent
                  title={MESSAGE.DATASET_UPLOAD_ERROR_FILE_NOT_SUPPORTED_TITLE}
                  description={MESSAGE.DATASET_UPLOAD_ERROR_FILE_NOT_SUPPORTED_DESCRIPTION(
                    fileTypesText
                  )}
                />
              ),
              autoClose: 2000
            });
          }
        });
      });
    },
    onDropAccepted: (acceptedFiles) => {
      if (!navigator.onLine) {
        showToast({
          type: EToastType.ERROR,
          message: (
            <NotificationContent
              title={MESSAGE.NO_INTERNET_CONNECTION_TITLE}
              description={MESSAGE.NO_INTERNET_CONNECTION_DESCRIPTION}
            />
          ),
          autoClose: 2000
        });
        return;
      }
      const controller = new AbortController();
      setAbortController(controller);

      const formData = new FormData();
      if (isShapefileUploadEnabled) {
        acceptedFiles.forEach((f) => formData.append("files", f));
      } else {
        const file = acceptedFiles[0];
        if (!file) return;
        formData.append("file", file);
      }

      const { mutate } = uploadMutation;

      (
        mutate as UseMutateFunction<
          { operation_id: string },
          Error,
          | {
              file: FormData;
              signal?: AbortSignal;
              isShapefileUploadEnabled?: boolean;
            }
          | {
              file: FormData;
              signal?: AbortSignal;
              id: string;
              isShapefileUploadEnabled?: boolean;
            }
        >
      )(
        uploadType === EUploadDatasetType.NEW
          ? {
              file: formData,
              signal: controller.signal,
              isShapefileUploadEnabled
            }
          : {
              file: formData,
              signal: controller.signal,
              id: datasetId!,
              isShapefileUploadEnabled
            }
      );
    }
  });

  const handleCancelUpload = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }

    setIsUploading(false);
    uploadMutation.reset();
  };

  // Render upload progress UI
  const renderUploadProgress = () => {
    return (
      <div className="border-2 border-transparent rounded-lg p-8 text-center transition-colors min-h-[200px] flex flex-col items-center justify-center">
        <SpinnerIcon
          className="spin-animation"
          data-testid="upload-progress-spinner"
          width={60}
          height={60}
        />

        <button
          type="button"
          onClick={handleCancelUpload}
          data-testid="cancel-upload-button"
          className="mt-3 text-royal-blue text-[10px] font-semibold"
        >
          Cancel
        </button>
      </div>
    );
  };

  useEffect(() => {
    if (setIsLoading) setIsLoading(isUploading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading]);

  // Effect to cancel upload on component unmount
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);

  const displayedFileTypes: ExportDatasetFileType[] = allowedFileTypes.filter(
    (type) => {
      if (type === ExportDatasetFileType.GEOJSON && !isGeoJsonUploadEnabled)
        return false;
      if (type === ExportDatasetFileType.SHAPEFILE && !isShapefileUploadEnabled)
        return false;
      if (type === ExportDatasetFileType.KML && !isKMLUploadEnabled)
        return false;
      return true;
    }
  );

  // Format the allowed file types into a readable string for the upload area display text
  const displayedFileTypeLabels = displayedFileTypes.map(
    (type) => fileTypeLabels[type]
  );

  // Format the allowed file types into a readable string for the upload area display text
  const fileTypesText =
    displayedFileTypeLabels.length > 2
      ? `${displayedFileTypeLabels.slice(0, -1).join(", ")}, or ${displayedFileTypeLabels.slice(-1)}`
      : displayedFileTypeLabels.join(" or ");

  return (
    <DialogContent
      data-testid="upload-dataset-modal"
      data-tourid="bring-your-own-data-upload-dropzone"
      // Keep the dialog open while the tour drives it: block Radix's
      // Escape/interact-outside/focus-outside auto-dismissal. Normal usage
      // (preventAutoDismiss=false) keeps the default dismiss behavior.
      onEscapeKeyDown={
        preventAutoDismiss ? (event) => event.preventDefault() : undefined
      }
      onInteractOutside={
        preventAutoDismiss ? (event) => event.preventDefault() : undefined
      }
      disableAnimation={disableAnimation}
    >
      <DialogHeader className="space-y-1 text-left">
        <DialogTitle className="text-txtHeading text-[20px] font-semibold text-left">
          {title[uploadType]}
        </DialogTitle>
        <DialogDescription className="text-txtBody text-[12px]">
          {MESSAGE.UPLOAD_DIALOG_DESCRIPTION}
        </DialogDescription>
        {(uploadType === EUploadDatasetType.ADD_MORE ||
          uploadType === EUploadDatasetType.REPLACE_ALL) && (
          <Alert className="flex flex-row text-[10px] text-txtBody">
            <InformationIcon
              className="fill-royal-blue"
              width={20}
              height={20}
            />
            <AlertDescription className="!translate-y-0">
              {uploadType === EUploadDatasetType.ADD_MORE
                ? "You can only add data with the same data structure."
                : "You can replace all existing data with a new dataset. The new dataset must have the same data structure."}
            </AlertDescription>
          </Alert>
        )}
      </DialogHeader>
      {isUploading ? (
        renderUploadProgress()
      ) : (
        <div
          {...getRootProps()}
          className="border-2 border-dashed border-[#888888] rounded-lg p-8 text-center cursor-pointer hover:border-[#4C7DFB] transition-colors"
          style={{
            minHeight: "200px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <input {...getInputProps()} data-testid="upload-input" />
          <div className="mb-2">
            <FilePlusIcon className="w-8 h-8 fill-light-gray" />
          </div>
          <p className="text-[16px] font-semibold mb-2">
            <span className="text-[#4C7DFB]">Choose File</span>
            <span className="text-[#616161]"> or Drag and Drop</span>
          </p>
          <p className="text-[10px] text-[#888888] font-normal">
            You can upload{" "}
            <span className="font-bold text-black">{fileTypesText}</span> (max.
            100 MB).
          </p>
        </div>
      )}
      {isShapefileUploadEnabled && (
        <div className="flex flex-col text-[10px]">
          <p className="text-txtPlaceholder font-semibold">Shapefile Note:</p>
          <span>{MESSAGE.SHAPEFILE_NOTE}</span>
        </div>
      )}
    </DialogContent>
  );
}
